'use strict';

import type { DeleteResponse } from '../CoreRepository';
import { CoreRepository } from '../CoreRepository';
import { UserMeta } from '../../Entities/User/UserMeta';
import { UserMetaCollection } from '../../Collections/User/UserMetaCollection';
import type { User } from '../../Entities/User/User';
import { Utilities } from '../../Utilities/Utilities';

/**
 * Complex type to be used in the SDK.
 * Represents a fully hydrated `UserMeta` including a `User` entity and JS-native types.
 */
export interface UserMetaSchema {
  /** Owning user entity. */
  user?: User;
  /** Metadata key (e.g., "favouriteColour"). */
  key: string;
  /** String value, if stored as string. */
  valueString?: string | null;
  /** Integer value, if stored as number. */
  valueInt?: number | null;
  /** Boolean value, if stored as boolean. */
  valueBool?: boolean | null;
  /** JSON value, if stored as object/array. */
  valueJSON?: any | null;
  /** Last update timestamp as a Date. */
  updatedAt: Date | null;
}

/**
 * Simple type for moving data between the SDK and the API.
 * Uses plain primitives, IDs, and ISO date strings.
 */
export interface UserMetaPayload {
  /** User ID (foreign key). */
  user?: number;
  /** Metadata key. */
  key?: string;
  /** String value. */
  valueString?: string | null;
  /** Integer value. */
  valueInt?: number | null;
  /** Boolean value. */
  valueBool?: boolean | null;
  /** JSON value (object/array/primitive). */
  valueJSON?: any | null;
  /** ISO-8601 string for updatedAt. */
  updatedAt?: string | null;
}

/**
 * Simplified schema for updating and inserting meta data
 */
export interface UserMetaSchemaSimple {
  /** Metadata key. */
  key: string;
  /** Metadata value. */
  value: any;
}

/**
 * Simplified payload for updating and inserting meta data
 */
export interface UserMetaPayloadSimple {
  /** Metadata key. */
  key: string;
  /** Metadata value. */
  value: any;
}

/**
 * Format when returning a list of UserMeta entities from the API.
 */
export interface UserMetaCollectionPayload {
  /** Page limit. */
  limit?: number;
  /** Total number of records. */
  total?: number;
  /** Page offset. */
  offset?: number;
  /** Page data. */
  data?: Array<UserMetaPayload>;
}

/**
 * Repository for UserMeta entities. Handles transformation between SDK models
 * and API payloads, and exposes CRUD-style operations.
 */
export class UserMetaRepository extends CoreRepository {
  /**
   * Takes data supplied in the UserMetaSchema format and converts it to a simplified version
   * for use with the API.
   *
   * Only properties present on the input are copied, allowing partial updates.
   *
   * @param {UserMetaSchema} data - Data to be converted to a simplified format for usage with the API.
   * @returns {UserMetaPayload} Simplified data structure for use with the API.
   */
  protected processData(data: UserMetaSchemaSimple): UserMetaPayloadSimple {
    const payload: UserMetaPayloadSimple = {
      key: data.key,
      value: data.value,
    };

    return payload;
  }

  /**
   * Retrieve a paginated list of UserMeta items for a given user.
   *
   * @param {number} userId - User ID to fetch metadata for.
   * @param {object} [options={}] - Query options such as limit, offset, sortBy, order.
   * @param {object|null} [criteria=null] - Additional filter criteria passed to the API.
   * @returns {Promise<UserMetaCollection>} A hydrated collection with pagination info.
   */
  public getList(
    userId: number,
    options: object = {},
    criteria: object = null
  ): Promise<UserMetaCollection> {
    if (!options) options = {};
    if (criteria) (options as any)['criteria'] = criteria;

    return this.webAPI
      .getUserMetaList(userId, options)
      .then((response: UserMetaCollectionPayload & { data: any[] }) => {
        const collection = new UserMetaCollection();

        response.data.forEach((collectionItemData: any) => {
          const collectionItem = new UserMeta();
          collectionItem.hydrate(collectionItemData);
          collection.addItem(collectionItem);
        });

        collection.setOffset(response.offset ?? 0);
        collection.setLimit(response.limit ?? 0);
        collection.setTotal(response.total ?? 0);

        return collection;
      });
  }

  /**
   * Retrieve a single UserMeta by key.
   *
   * @param {number} userId - User ID.
   * @param {string} userMetaKey - Metadata key to fetch.
   * @param {object} [options={}] - Optional query options.
   * @returns {Promise<UserMeta>} Hydrated `UserMeta` instance.
   */
  public get(userId: number, userMetaKey: string, options = {}): Promise<UserMeta> {
    return this.webAPI.getUserMeta(userId, userMetaKey, options).then((response: any) => {
      const item = new UserMeta();
      item.hydrate(response);
      return item;
    });
  }

  /**
   * Update or create (upsert) a single UserMeta by key.
   *
   * @param {number} userId - User ID.
   * @param {UserMetaPayloadSimple} data - Data to send to the API (partial is allowed).
   * @returns {Promise<UserMeta>} Hydrated `UserMeta` instance after update.
   */
  public update(userId: number, data: UserMetaSchemaSimple): Promise<UserMeta> {
    const payload = this.processData(data);

    return this.webAPI.updateUserMeta(userId, payload).then((response: any) => {
      const item = new UserMeta();
      item.hydrate(response);
      return item;
    });
  }

  /**
   * Delete a single UserMeta by key.
   *
   * Note: The `data` parameter is currently unused; kept for API parity/extensibility.
   *
   * @param {number} userId - User ID.
   * @param {string} userMetaKey - Metadata key to delete.
   * @param {UserMetaSchema} data - (Unused) Potential future payload for conditional deletes.
   * @returns {Promise<DeleteResponse>} Result of the delete operation.
   */
  public delete(
    userId: number,
    userMetaKey: string,
    data: UserMetaSchema
  ): Promise<DeleteResponse> {
    return this.webAPI.deleteUserMeta(userId, userMetaKey);
  }
}
